import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { publicAxios, baseUrl } from 'Api';
import AlterImage from 'Assets/alter-image.png';
import SharedVideoPlayer from './SharedVideoPlayer';
import styles from './SharedCategoryPlayer.module.css';

const PAGE_LIMIT = 12;
// Load the next page once the user scrolls within this many px of the bottom.
const SCROLL_THRESHOLD = 400;

// Format a duration given in seconds ("320") as m:ss / h:mm:ss.
const formatDuration = (raw) => {
  const total = Number(raw);
  if (!total || Number.isNaN(total)) return null;
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = Math.floor(total % 60);
  const pad = (n) => n.toString().padStart(2, '0');
  return hh ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
};

const thumbFor = (video) => video?.thumbnails?.[0]
  || video?.imagekit_icon
  || video?.thumbnail_hover
  || AlterImage;

// Public category viewer — a fresh, self-contained design (landscape hero +
// responsive card grid + infinite scroll). Not tied to the private inner page.
// Each video object already carries the exact playable shape SharedVideoPlayer
// expects, so it is reused unchanged when a tile is opened.
const SharedCategoryPlayer = ({ item, shareKey, ownerName }) => {
  const [videos, setVideos] = useState(() => (Array.isArray(item?.videos) ? item.videos : []));
  const [page, setPage] = useState(item?.page || 1);
  const [totalPages, setTotalPages] = useState(item?.total_pages || 1);
  const [loading, setLoading] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const wrapRef = useRef(null);
  // Synchronous guard so a burst of scroll events can't fire duplicate loads.
  const loadingRef = useRef(false);

  const totalCount = item?.video_count ?? videos.length;
  const hasMore = page < totalPages;

  // Fetch the next page and append it to the existing grid.
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !shareKey || page >= totalPages) return;
    loadingRef.current = true;
    const nextPage = page + 1;
    setLoading(true);
    try {
      const response = await publicAxios.post(
        `${baseUrl}/getSharedItem`,
        { key: shareKey, page: nextPage, limit: PAGE_LIMIT },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const data = response.data?.data?.item;
      if (response.data?.success && data) {
        const more = Array.isArray(data.videos) ? data.videos : [];
        setVideos((prev) => [...prev, ...more]);
        setPage(data.page || nextPage);
        if (data.total_pages) setTotalPages(data.total_pages);
      }
    } catch (err) {
      /* keep current list on failure */
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [shareKey, page, totalPages]);

  // Trigger a load as the user nears the bottom of the scroll container.
  const handleScroll = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD) {
      loadMore();
    }
  }, [loadMore]);

  // If the first page is too short to scroll (tall viewport), keep pulling
  // pages until the container overflows or we run out.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || loadingRef.current || page >= totalPages) return;
    if (el.scrollHeight <= el.clientHeight + 4) loadMore();
  }, [videos, page, totalPages, loadMore]);

  if (activeVideo) {
    return (
      <div className={styles.playerStage}>
        <SharedVideoPlayer item={activeVideo} onBack={() => setActiveVideo(null)} />
      </div>
    );
  }

  const openVideo = (video) => setActiveVideo(video);

  return (
    <div ref={wrapRef} className={styles.wrap} onScroll={handleScroll}>
      <div className={styles.inner}>
        {/* ---- Landscape hero ---- */}
        <section
          className={styles.hero}
          style={item?.icon ? { backgroundImage: `url(${item.icon})` } : undefined}
        >
          <div className={styles.heroScrim} />
          <div className={styles.heroContent}>
            <span className={styles.heroKicker}>Shared collection</span>
            <h1 className={styles.heroTitle}>{item?.title || 'Shared collection'}</h1>
            {item?.description ? (
              <p className={styles.heroDesc}>{item.description}</p>
            ) : null}
            <div className={styles.heroMeta}>
              <span>
                {totalCount}
                {' '}
                {totalCount === 1 ? 'video' : 'videos'}
              </span>
              {ownerName ? (
                <>
                  <span className={styles.dot} />
                  <span>{ownerName}</span>
                </>
              ) : null}
            </div>
          </div>
        </section>

        {/* ---- Grid ---- */}
        {videos.length === 0 ? (
          <div className={styles.empty}>This collection has no videos.</div>
        ) : (
          <div className={styles.gridWrap}>
            <div className={styles.grid}>
              {videos.map((video) => {
                const duration = formatDuration(video?.video_duration);
                return (
                  <div
                    key={video?.id ?? video?.draft_id}
                    className={styles.card}
                    role="button"
                    tabIndex={0}
                    onClick={() => openVideo(video)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openVideo(video);
                      }
                    }}
                  >
                    <div className={styles.thumbWrap}>
                      <img
                        src={thumbFor(video)}
                        alt={video?.title || ''}
                        className={styles.thumb}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = AlterImage; }}
                      />
                      <span className={styles.playBadge}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                      {duration && <span className={styles.duration}>{duration}</span>}
                    </div>
                    <div className={styles.cardBody}>
                      <span className={styles.cardTitle}>{video?.title || 'Untitled'}</span>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        )}

        {/* ---- Infinite-scroll status ---- */}
        {hasMore ? (
          <div className={styles.loadMore}>
            {loading && <div className={styles.spinner} />}
          </div>
        ) : (
          videos.length > PAGE_LIMIT && (
            <div className={styles.endNote}>You&apos;ve reached the end</div>
          )
        )}
      </div>
    </div>
  );
};

export default SharedCategoryPlayer;
